import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
    Zap, 
    X, 
    PieChart, 
    ArrowDownLeft, 
    TrendingUp,
    Bot,
    Wallet
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const QuickActionButton = () => {
    const [isOpen, setIsOpen] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();

    // Don't show on login/register pages
    if (location.pathname === '/login' || location.pathname === '/register') {
        return null;
    }

    const actions = [
        { 
            icon: PieChart, 
            label: 'Portfolio', 
            color: '#5BA4A4',
            action: () => navigate('/portfolio')
        },
        { 
            icon: ArrowDownLeft, 
            label: 'Deposit', 
            color: '#22c55e',
            action: () => navigate('/deposit')
        },
        { 
            icon: TrendingUp, 
            label: 'Dashboard', 
            color: '#3b82f6',
            action: () => navigate('/dashboard')
        },
        { 
            icon: Wallet, 
            label: 'Withdraw', 
            color: '#f59e0b',
            action: () => navigate('/withdraw')
        },
    ];

    // Hidden on mobile as per user request
    return (
        <div className="fixed left-4 bottom-24 z-[60] hidden" data-testid="quick-action-fab">
            <AnimatePresence>
                {isOpen && (
                    <>
                        {/* Backdrop */}
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[55]"
                            onClick={() => setIsOpen(false)}
                        />
                        
                        {/* Action buttons */}
                        <div className="absolute bottom-16 left-0 flex flex-col-reverse gap-3 z-[60]">
                            {actions.map((action, index) => (
                                <motion.button
                                    key={action.label}
                                    initial={{ opacity: 0, y: 20, scale: 0.8 }}
                                    animate={{ 
                                        opacity: 1, 
                                        y: 0, 
                                        scale: 1,
                                        transition: { delay: index * 0.05 }
                                    }}
                                    exit={{ 
                                        opacity: 0, 
                                        y: 20, 
                                        scale: 0.8,
                                        transition: { delay: (actions.length - index) * 0.03 }
                                    }}
                                    onClick={() => {
                                        action.action();
                                        setIsOpen(false);
                                    }}
                                    className="flex items-center gap-3 px-4 py-3 bg-[#0a0a0a]/95 border border-[rgba(91,164,164,0.3)] rounded-full shadow-lg backdrop-blur-sm"
                                    style={{ 
                                        boxShadow: `0 0 20px ${action.color}20`
                                    }}
                                    data-testid={`quick-action-${action.label.toLowerCase()}`}
                                >
                                    <span className="text-sm font-rajdhani font-semibold text-white whitespace-nowrap">
                                        {action.label}
                                    </span>
                                    <div 
                                        className="w-10 h-10 rounded-full flex items-center justify-center"
                                        style={{ backgroundColor: `${action.color}20` }}
                                    >
                                        <action.icon size={20} style={{ color: action.color }} />
                                    </div>
                                </motion.button>
                            ))}
                        </div>
                    </>
                )}
            </AnimatePresence>

            {/* Main FAB button */}
            <motion.button
                onClick={() => setIsOpen(!isOpen)}
                className={`relative w-14 h-14 rounded-full flex items-center justify-center shadow-xl transition-all duration-300 ${
                    isOpen 
                        ? 'bg-red-500 rotate-45' 
                        : 'bg-gradient-to-br from-[#5BA4A4] to-[#4A8F8F]'
                }`}
                whileTap={{ scale: 0.95 }}
                style={{
                    boxShadow: isOpen 
                        ? '0 0 30px rgba(239, 68, 68, 0.5)' 
                        : '0 0 30px rgba(91, 164, 164, 0.5)'
                }}
                data-testid="quick-action-toggle"
            >
                {/* Glow ring */}
                {!isOpen && (
                    <div className="absolute inset-0 rounded-full animate-ping bg-[#5BA4A4]/30" style={{ animationDuration: '2s' }} />
                )}
                
                <motion.div
                    animate={{ rotate: isOpen ? 45 : 0 }}
                    transition={{ duration: 0.2 }}
                >
                    {isOpen ? (
                        <X size={24} className="text-white" strokeWidth={2.5} />
                    ) : (
                        <Zap size={24} className="text-black" strokeWidth={2} />
                    )}
                </motion.div>
            </motion.button>
        </div>
    );
};

export default QuickActionButton;
